import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import { ReactSVG } from "react-svg";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";

import { formatPhone } from "../../../../helpers/functions";
import CaregiverCreate from "./CaregiverCreate";
import { deleteCaregiverDetails } from "../../../../redux/modules/patient/actions";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import Alert from "../../../common/Alert";
import { stateOptions } from "../../create/options";
import { capitalizeFirstLetter } from "../../../../helpers/functions";

const CaregiverInformation = ({ patient }) => {
  const dispatch = useDispatch();
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [create, setCreate] = useState(false);
  const [currentCaregiver, setCurrentCaregiver] = useState(null);
  const [noCareGiver, setNoCaregiver] = useState(
    !(patient.caregiver && patient.caregiver.length > 0)
  );
  const [buttonHeight, setButtonHeight] = useState(550);

  const deleteCaregiver = (id) => {
    dispatch(deleteCaregiverDetails(patient.id, id));
  };
  const [idToDelete, setIdToDelete] = useState(null);
  const [openDeleteCaregiver, setOpenDeleteCaregiver] = useState(false);
  const caregiverValueLabelOptions = [].concat(
    patient.caregiver.map((option) => ({
      value: option.id,
      label:
        option.lastName +
        " " +
        (option.middleName?.length > 0 ? " " : "") +
        option.firstName,
    }))
  );

  const [isOverflowing, setIsOverflowing] = useState(false);
  useEffect(() => {
    const myElement = document.getElementById("alternate-caregiver");
    if (myElement) {
      setIsOverflowing(myElement.scrollWidth > myElement.clientWidth);
      setEnableRightArrow(true);
    }
    setNoCaregiver(!(patient.caregiver && patient.caregiver.length > 0));
  }, [patient.caregiver]);

  const updateButtonHeight = (element) => {
    if (element) setButtonHeight(element.offsetHeight || 550);
  };

  useEffect(() => {
    const handleResize = () => {
      const firstCard = document.querySelector('.mainCard');
      if (firstCard) updateButtonHeight(firstCard);
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [patient.caregiver, showCreateSlider]);

  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const handleLeftScroll = () => {
    const element = document.getElementById("alternate-caregiver");
    element.scrollLeft -= 400;
    // at top right scroll - scrollLeft + clientWidth will be equal to scrollWidth
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    // at top left scroll - scrollLeft will be equal to 0
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const [enableRightArrow, setEnableRightArrow] = useState(false);
  const handleRightScroll = () => {
    const element = document.getElementById("alternate-caregiver");
    element.scrollLeft += 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  return (
    <>
      <div className="relative w-full">
        {noCareGiver && (
          <Alert
            title="No Alternate Caregiver Information"
            type="warning"
            className="mb-4"
            isWrapped={true}
            showTexture={false}
            handleClose={() => setNoCaregiver(false)}
          />
        )}

        <div
          className="flex gap-[30px] overflow-x-auto h-full min-h-[550px] scroll-smooth"
          id="alternate-caregiver"
        >
          {patient.caregiver.map((item, i) => {
            const contactInfo = item.contactInfo;
            const careGiver = { ...item, ...item.contactInfo };
            return (
              <div
                key={i}
                ref={i === 0 ? (el) => updateButtonHeight(el) : null}
                className="mainCard min-w-[500px] w-auto flex-none"
              >
                {!create && (
                  <CaregiverCreate
                    open={currentCaregiver === item.id && showCreateSlider}
                    onClose={(value) => {
                      setShowCreateSlider(value);
                    }}
                    careGiver={careGiver}
                    patient={patient}
                    patientId={patient.id}
                  />
                )}
                <div className="flex items-center justify-between h-[72px] px-6 py-3 border-b border-gray-100 bg-gray-20">
                  <div className="flex flex-col gap-1">
                    <h4 className="text-lg font-bold text-teal-600">
                      {capitalizeFirstLetter(item.lastName)},{" "}
                      {capitalizeFirstLetter(item.firstName)}{" "}
                      {capitalizeFirstLetter(item.middleName)}
                    </h4>
                  </div>
                  <div>
                    <Menu as="div" className="relative inline-block text-left">
                      <div>
                        <Menu.Button>
                          <ReactSVG
                            src={`/icons/triple-dot.svg`}
                            className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]`}
                          />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform translate-y-2 opacity-0 scale-95"
                        enterTo="transform translate-y-0 opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform translate-y-0 opacity-100 scale-100"
                        leaveTo="transform translate-y-2 opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                          <Menu.Item>
                            <button
                              className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                              onClick={() => {
                                setShowCreateSlider(true);
                                setCurrentCaregiver(item.id);
                                setCreate(false);
                              }}
                            >
                              Edit
                            </button>
                          </Menu.Item>
                          <Menu.Item>
                            <button
                              className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                              onClick={() => {
                                setOpenDeleteCaregiver(true);
                                setIdToDelete(item.id);
                              }}
                            >
                              Delete
                            </button>
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                </div>
                <div className="px-6 py-3.5 flex flex-col h-full">
                  <div className="flex-1 overflow-y-auto pr-2">
                    <div className="grid grid-cols-1">
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">
                          Relationship
                        </p>
                        <div className="flex">
                          <p className="text-body-md font-bold text-gray-800 capitalize relative top-0.5">
                            {item.relationship || ""} &nbsp;
                          </p>
                          <p>
                            {contactInfo.primaryContact ? (
                              <span
                                className={`px-2 py-1 w-max font-medium bg-teal-75 text-teal-300 text-[10px] border rounded-full`}
                              >{`Primary Contact`}</span>
                            ) : (
                              <span
                                className={`px-2 py-1 w-max font-medium bg-gray-50 text-teal-300 text-[10px] border rounded-full`}
                              >{`Secondary Contact`}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">
                          Primary Phone Number
                        </p>
                        <p className="font-bold text-gray-800 text-body-md">
                          {contactInfo.primaryPhone ? formatPhone(contactInfo.primaryPhone) : ""}
                        </p>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">
                          Secondary Phone Number
                        </p>
                        <p className="font-bold text-gray-800 text-body-md">
                          {contactInfo.secondaryPhone ? formatPhone(contactInfo.secondaryPhone) : ""}
                        </p>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">Email</p>
                        <p className="font-bold text-gray-800 text-body-md">
                          {contactInfo?.email || ""}
                        </p>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div>
                        <p className="mb-1.5 text-sm text-gray-700">
                          Address
                        </p>
                        <p className="font-bold text-gray-800 text-body-md">
                          {contactInfo?.address
                            ? `${contactInfo?.address}${contactInfo?.poBox ? `, PO Box: ${contactInfo?.poBox}` : ""}${contactInfo?.unitNumber ? `, Unit # ${contactInfo?.unitNumber}` : ""}`
                            : ""}
                        </p>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div className="grid grid-cols-2 justify-items-start">
                        <div>
                          <p className="mb-1.5 text-sm text-gray-700">
                            City/Town
                          </p>
                          <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                            {contactInfo?.city || ""}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1.5 text-sm text-gray-700">
                            Province
                          </p>
                          <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                            {stateOptions.find(
                              (option) => option.value === contactInfo?.province
                            )?.label || ""}
                          </p>
                        </div>
                      </div>
                      
                      <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                      <div className="grid grid-cols-2 justify-items-start">
                        <div>
                          <p className="mb-1.5 text-sm text-gray-700">
                            Postal Code
                          </p>
                          <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                            {contactInfo?.postalCode 
                              ? contactInfo.postalCode.replace(/\s/g, '').replace(/(.{3})(.*)/, '$1 $2').trim()
                              : ""}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1.5 text-sm text-gray-700">
                            Country
                          </p>
                          <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                            {contactInfo?.country ? capitalizeFirstLetter(contactInfo.country) : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0 border-t border-gray-200 pt-3 mt-3">
                    <div className="col-span-full">
                      <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                      <p className="text-body-md font-bold text-gray-800 capitalize min-h-[22px]">
                        {contactInfo?.comments || ""}
                      </p>
                    </div>
                  </div>
                </div>
                <ConfirmationModalNew
                  confirmText="Confirm"
                  type="primary"
                  title="Delete Caregiver"
                  content={() =>
                    "Are you sure you want to delete this Caregiver?"
                  }
                  optionLabel="Alternate Caregiver Name"
                  confirmAction={() => {
                    deleteCaregiver(idToDelete);
                    setOpenDeleteCaregiver(false);
                    setIdToDelete(null);
                  }}
                  open={openDeleteCaregiver}
                  handleClose={() => setOpenDeleteCaregiver(false)}
                  onCross={() => setOpenDeleteCaregiver(false)}
                  options={caregiverValueLabelOptions}
                  selectedOption={idToDelete}
                />
              </div>
            );
          })}
          {
            <button
              onClick={() => {
                setShowCreateSlider(true);
                setCreate(true);
              }}
              className="inline-flex justify-center items-center flex-col min-w-[500px] w-auto flex-none p-4 align-top bg-white border border-dashed rounded border-REELTeal text-lg font-medium text-REELTeal"
              style={{ height: `${buttonHeight}px`, minHeight: `${buttonHeight}px` }}
            >
              <PlusIcon className="w-6 h-6 text-REELTeal" />
              <span className="font-bold text-REELTeal text-body-md mt-2">
                Add Alternate Caregiver
              </span>
            </button>
          }
          {create && (
            <CaregiverCreate
              open={showCreateSlider}
              onClose={(value) => {
                setShowCreateSlider(value);
              }}
              patient={patient}
              create={create}
              patientId={patient.id}
            />
          )}
        </div>
        {isOverflowing && (
          <>
            {enableLeftArrow && (
              <button
                className="absolute m-auto left-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                onClick={handleLeftScroll}
              >
                <ReactSVG
                  src="/icons/arrow-right.svg"
                  className="w-[15px] rotate-180"
                />
              </button>
            )}
            {enableRightArrow && (
              <button
                className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px]  top-0 bottom-0 h-fit text-teal-600 font-bold"
                onClick={handleRightScroll}
              >
                <ReactSVG src="/icons/arrow-right.svg" className="w-[15px]" />
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CaregiverInformation;
